import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MousePointerClick, Sparkles, Layers } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal, EASE } from "./Reveal";
import { scrollToId } from "../../lib/scroll";
import { useSection } from "../../lib/content";
import { trackCta } from "../../lib/track";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const SingleCard = ({ active }) => (
  <>
    <div className="relative flex-1">
      <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#FF5050]">
        <Sparkles className="w-4 h-4" />
        Your recommended Gen 2 path
      </p>
      <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
        {active.gen1} <span className="text-slate-500 mx-1">→</span> <span className="text-[#FF5050]">{active.gen2}</span>
      </h3>
      <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed max-w-xl">{active.benefit}</p>
    </div>
    <Cta label="Connect with your HP Poly specialist" />
  </>
);

const FleetCard = ({ selected, mappings }) => {
  const picks = mappings.filter((m) => selected.includes(m.gen1));
  return (
    <>
      <div className="relative flex-1 w-full">
        <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#FF5050]">
          <Layers className="w-4 h-4" />
          Your fleet migration summary · {picks.length} devices
        </p>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {picks.map((m) => (
            <div key={m.gen1} className="rounded-xl border border-[#2a2a2a] bg-[#242424] px-5 py-4">
              <p className="font-display font-bold text-white tracking-tight">
                {m.gen1} <span className="text-slate-500 mx-1">→</span> <span className="text-[#FF5050]">{m.gen2}</span>
              </p>
              <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{m.benefit}</p>
            </div>
          ))}
        </div>
      </div>
      <Cta label="Connect with your HP Poly specialist" />
    </>
  );
};

const Cta = ({ label }) => (
  <button
    data-testid="recommender-cta"
    onClick={() => { trackCta("matrix-recommender"); scrollToId("contact"); }}
    className="group relative shrink-0 inline-flex items-center gap-2.5 bg-[#024AD8] hover:bg-[#0238A8] text-white font-semibold px-7 py-3.5 rounded-md transition-[background-color,transform] duration-300 active:scale-[0.98] lg:self-center"
  >
    {label}
    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

export const Matrix = ({ selected, onToggle }) => {
  const c = useSection("matrix");
  const mappings = c.mappings;
  const hasSelection = selected.length > 0;

  return (
    <section id="matrix" className="relative bg-white py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHead chapter="07" eyebrow={c.eyebrow} title={c.title} lead={c.lead} />

        <Reveal delay={0.1}>
          <div className="mt-12">
            <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-4">
              <MousePointerClick className="w-4 h-4 text-[#024AD8]" />
              Select your current Gen 1 devices — pick one or more
            </p>
            <div className="flex flex-wrap gap-3">
              {mappings.map((m) => {
                const isOn = selected.includes(m.gen1);
                return (
                  <button
                    key={m.gen1}
                    data-testid={`device-picker-${slug(m.gen1)}`}
                    onClick={() => onToggle(m.gen1)}
                    aria-pressed={isOn}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-[background-color,color,border-color,transform] duration-300 active:scale-[0.97] ${
                      isOn
                        ? "bg-[#024AD8] text-white border-[#024AD8]"
                        : "bg-white text-slate-700 border-slate-300 hover:border-slate-900 hover:-translate-y-0.5"
                    }`}
                  >
                    {m.gen1}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="replacement-matrix-table" className="mt-8 rounded-2xl border border-[#2a2a2a] overflow-hidden shadow-[0_24px_60px_-30px_rgba(15,23,42,0.25)]">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center bg-[#1a1a1a] text-white px-7 py-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-400">Current Gen 1 Device</span>
              <span className="w-10" />
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-[#6D8FF0] text-right">Gen 2 Replacement</span>
            </div>
            {mappings.map((m, i) => {
              const isActive = selected.includes(m.gen1);
              return (
                <button
                  key={m.gen1}
                  onClick={() => onToggle(m.gen1)}
                  className={`group w-full text-left grid grid-cols-[1fr_auto_1fr] items-center px-7 py-6 transition-[background-color] duration-300 cursor-pointer ${
                    isActive
                      ? "bg-[#16233F] shadow-[inset_4px_0_0_#024AD8]"
                      : `${i % 2 === 0 ? "bg-[#1a1a1a]" : "bg-[#202020]"} hover:bg-[#232323]`
                  }`}
                >
                  <span className={`font-display font-bold text-lg tracking-tight transition-colors duration-300 ${isActive ? "text-white" : "text-slate-400 group-hover:text-slate-200"}`}>
                    {m.gen1}
                  </span>
                  <span className="w-10 flex justify-center">
                    <span className={`w-9 h-9 rounded-full border grid place-items-center transition-[background-color,border-color,transform] duration-300 ${
                      isActive ? "bg-[#024AD8] border-[#024AD8] scale-110" : "border-slate-200 bg-white group-hover:bg-[#024AD8] group-hover:border-[#024AD8] group-hover:scale-110"
                    }`}>
                      <ArrowRight className={`w-4 h-4 transition-colors duration-300 ${isActive ? "text-white" : "text-slate-400 group-hover:text-white"}`} />
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="inline-flex items-center gap-2 font-display font-bold text-white text-lg tracking-tight">
                      {m.gen2}
                      <span className={`font-mono text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-full ${
                        isActive ? "bg-[#024AD8] text-white" : "bg-[#024AD8]/15 text-[#8FA8F5]"
                      }`}>
                        Gen 2
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {hasSelection && (
            <motion.div
              key={selected.length > 1 ? "fleet" : selected[0]}
              data-testid="recommender-result"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.98 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="mt-8 rounded-2xl bg-[#1a1a1a] p-8 lg:p-10 flex flex-col lg:flex-row items-start gap-6 lg:gap-12 overflow-hidden relative"
            >
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#FF5050]/10 blur-3xl pointer-events-none" />
              {selected.length > 1
                ? <FleetCard selected={selected} mappings={mappings} />
                : <SingleCard active={mappings.find((m) => m.gen1 === selected[0])} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
